import { useCallback, useEffect } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { organizeModes } from '../helpers'
import useImageOrganizeContext from './useImageOrganizeContext'

/**
 * Returns true if params and permissions are valid for image organize
 */
const useImageOrganizeModes = ({ unauthorizedCallback } = {}) => {
  const { query, setQuery } = useQueryParams({ replaceState: true })
  const { hasImageOrganizePermissions, imageOrganizePermissionsLoaded } = useImageOrganizeContext()

  const { view, mode } = query
  const isImageOrganizeMode = view === 'organize'
  const isDivideMode = isImageOrganizeMode && mode === organizeModes.DIVIDE
  const isMetadataAllMode = isImageOrganizeMode && mode === organizeModes.METADATA_ALL
  const isMetadataSingleMode = isImageOrganizeMode && mode === organizeModes.METADATA

  const switchToExploreView = useCallback(() => {
    setQuery({ view: 'explore', mode: undefined })
  }, [setQuery])

  const switchToDivideMode = useCallback(() => {
    setQuery({
      view: 'organize',
      mode: 'DIVIDE',
      grid: 'on',
    })
  }, [setQuery])

  /* Check permissions. If unauthorized, fallback to explore view */
  useEffect(() => {
    if (isImageOrganizeMode && imageOrganizePermissionsLoaded && !hasImageOrganizePermissions) {
      if (typeof unauthorizedCallback === 'function') {
        unauthorizedCallback()
      }
      switchToExploreView()
    }
  }, [
    hasImageOrganizePermissions,
    isImageOrganizeMode,
    switchToExploreView,
    imageOrganizePermissionsLoaded,
    unauthorizedCallback,
  ])

  /* Default to metadata mode if an invalid mode is provided */
  useEffect(() => {
    if (isImageOrganizeMode && !Object.values(organizeModes).find((val) => val === mode)) {
      setQuery({ mode: organizeModes.METADATA })
    }
  }, [mode, isImageOrganizeMode, setQuery])

  const goBackToSearchResults = useCallback(
    (previousGroupId, dgs = null) => {
      const queryObj = JSON.parse(localStorage.getItem('ly.images.searchResults.lastQuery') || '{}')
      if (Object.keys(queryObj).length) {
        queryObj.needsToBeOrganized = true
        let id = previousGroupId

        if (previousGroupId || dgs) {
          localStorage.setItem('leavingMode', 'divide')
          if (isMetadataAllMode || isMetadataSingleMode) {
            localStorage.setItem('leavingMode', isMetadataAllMode ? 'metadata_all' : 'metadata')
            if (dgs) id = dgs
          }
          localStorage.setItem('previousGroupId', id)
        }
      }

      const urlQuery = new URLSearchParams(queryObj).toString()
      window.location.href = `${window.location.origin}/records/images/search-results${urlQuery ? `?${urlQuery}` : ''}`
    },
    [isMetadataAllMode, isMetadataSingleMode]
  )

  return {
    goBackToSearchResults,
    isImageOrganizeMode,
    isDivideMode,
    isMetadataSingleMode,
    isMetadataAllMode,
    switchToExploreView,
    switchToDivideMode,
  }
}

export default useImageOrganizeModes
